import { add, divide, multiply, round, subtract } from 'vend-number'

export type PriceComponents = {
  price: string | number
  tax: string | number
}

export type DiscountInfo = {
  discount: string | number
  discountPercent: string | number
  markupPercent: string | number
}

/**
 * Calculate the individual price and tax components for a given tax inclusive price.
 *
 * @param  taxInclusivePrice The tax inclusive price
 * @param  taxRate The tax rate
 *
 * @return {PriceComponents} The `price` and `tax` components for the given price (unrounded for accuracy)
 */
export function calculateTaxInclusivePriceComponents(
  taxInclusivePrice: number | string,
  taxRate: number | string
): PriceComponents {
  const price = divide(taxInclusivePrice, add(1, taxRate))
  const tax = subtract(taxInclusivePrice, price)

  // Return tax inclusive price components as non-rounded to prevent rounding issues further down the line
  return {
    tax,
    price,
  }
}

/**
 * Calculate the individual price and tax components for a given tax exclusive price.
 *
 * @param  taxExclusivePrice The tax exclusive price
 * @param  taxRate The tax rate
 *
 * @return {PriceComponents} The `price` and `tax` components for the given price to 5 d.p.
 */
export function calculateTaxExclusivePriceComponents(
  taxExclusivePrice: number | string,
  taxRate: number | string
): PriceComponents {
  const tax = multiply(taxExclusivePrice, taxRate)

  return {
    tax: round(tax, 5),
    price: round(taxExclusivePrice, 5),
  }
}

/**
 * Returns the appropriate individual price and tax components (either inclusive or exclusive).
 *
 * @param  taxAppropriatePrice The price appropriate to the current store (i.e. tax inclusive for a tax inclusive store)
 * @param  taxRate The tax rate
 * @param  isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @return {PriceComponents} The `price` and `tax` components for the given price
 */
export function calculatePriceComponents(
  taxAppropriatePrice: number | string,
  taxRate: number | string,
  isTaxInclusive: boolean
): PriceComponents {
  if (isTaxInclusive) {
    return calculateTaxInclusivePriceComponents(taxAppropriatePrice, taxRate)
  } else {
    return calculateTaxExclusivePriceComponents(taxAppropriatePrice, taxRate)
  }
}

/**
 * Calculate the appropriate (tax inclusive/exclusive) price based on the given price and tax and whether the current
 * store is tax inclusive or tax exclusive.
 *
 * @note The result is not rounded.
 *
 * @param  price The tax exclusive price
 * @param  tax The corresponding tax amount
 * @param  isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @return {string | number} The appropriate price based on the given price and tax and isTaxInclusive
 */
export function calculateTaxAppropriatePrice(
  price: number | string,
  tax: number | string,
  isTaxInclusive: boolean
): string | number {
  if (isTaxInclusive) {
    return add(price, tax)
  } else {
    return price
  }
}

/**
 * Calculate the unrounded discount and related discount percent and markup percent based on the given prices and
 * taxes as appropriate for a tax inclusive or tax exclusive merchant.
 *
 * @param basePrice The base price of the line item
 * @param baseTax The base tax of the line item
 * @param price The sale price of the line item
 * @param tax The sale tax of the line item
 * @param isTaxInclusive Whether the current store is tax inclusive or tax exclusive
 *
 * @returns {DiscountInfo} The unrounded discount, discountPercent and markupPercent calculated based on the given
 *          prices and taxes
 */
export function calculateUnroundedDisplayDiscountInfo(
  basePrice: string | number,
  baseTax: string | number,
  price: string | number,
  tax: string | number,
  isTaxInclusive: boolean
): DiscountInfo {
  const displayBasePrice = calculateTaxAppropriatePrice(
    basePrice,
    baseTax,
    isTaxInclusive
  )
  const displayPrice = calculateTaxAppropriatePrice(price, tax, isTaxInclusive)
  const discountValue = subtract(displayBasePrice, displayPrice)
  const markupValue = subtract(displayPrice, displayBasePrice)

  let discountDecimal = 0
  let markupDecimal = 0

  if (parseFloat(displayBasePrice as string) !== 0) {
    discountDecimal = divide(discountValue, displayBasePrice)
    markupDecimal = divide(markupValue, displayBasePrice)
  }

  const discountPercent = parseFloat(round(multiply(discountDecimal, 100), 2))
  const markupPercent = parseFloat(round(multiply(markupDecimal, 100), 2))

  return {
    discount: discountValue,
    discountPercent,
    markupPercent,
  }
}
